import React, { useState, useEffect } from 'react';
import { getPlayers, getOpponents, getScenarios, getLoadScenarios, getPlayerMatchDrills } from '../../api';

// ── Shared constants ──────────────────────────────────────────────────────────
const MATCH_TARGETS = {
  CB:  { TD: 10675, HID:  592, SD: 136, HMLD: 1776, SA: 128 },
  FB:  { TD: 11163, HID: 1032, SD: 285, HMLD: 2542, SA: 185 },
  CDM: { TD: 12259, HID:  828, SD: 140, HMLD: 2253, SA: 165 },
  CAM: { TD: 11793, HID:  797, SD: 133, HMLD: 2222, SA: 167 },
  ST:  { TD: 10733, HID:  936, SD: 223, HMLD: 2233, SA: 157 },
  W:   { TD: 11035, HID: 1025, SD: 239, HMLD: 2535, SA: 187 },
};
const BUILT_IN_SCENARIOS = ['1A','1B','1C','1D','1E','2A','2B','2C','3A','3B','3C','3D','4A','4B','4C','5A','6A','6B'];

const parseTimeToMinutes = t => {
  if (!t) return 0;
  const p = String(t).trim().split(':').map(Number);
  if (p.length === 3) return p[0] * 60 + p[1] + p[2] / 60;
  if (p.length === 2) return p[0] + p[1] / 60;
  return 0;
};

function findMatchScenario(scenarios, playerId, matchDate) {
  if (!matchDate) return null;
  const active = sc => sc.startDate <= matchDate;
  const pl = scenarios.filter(sc => String(sc.playerId) === String(playerId) && active(sc));
  if (pl.length) return pl.sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
  const tm = scenarios.filter(sc => sc.playerId === 'team' && active(sc));
  return tm.length ? tm.sort((a, b) => b.startDate.localeCompare(a.startDate))[0] : null;
}

function computeMatchRows(playerDrills, player, opponents, scenarios, customTemplates) {
  const bySession = {};
  (playerDrills || []).forEach(d => {
    if (!bySession[d.session_id]) {
      bySession[d.session_id] = {
        sessionId: d.session_id,
        date: (d.session_date || '').slice(0, 10),
        title: d.session_title || '',
        opponentId: d.opponent_id,
        drills: [],
      };
    }
    bySession[d.session_id].drills.push(d);
  });
  return Object.values(bySession).map(s => {
    const opp = opponents.find(o => o.id === s.opponentId);
    const sc  = findMatchScenario(scenarios, player?.id, s.date);
    const pos = player?.position;
    let tgt   = null;
    if (sc && pos) {
      const tmpl = customTemplates.find(t => t.name === sc.scenario);
      if (tmpl) {
        const mdRow = tmpl.targets?.['MD']?.[pos];
        if (mdRow) tgt = { TD: mdRow.TD?.lower, HID: mdRow.HID?.lower, SD: mdRow.SD?.lower, HMLD: mdRow.HMLD?.lower, SA: mdRow.SA?.lower };
      } else if (BUILT_IN_SCENARIOS.includes(sc.scenario)) {
        tgt = MATCH_TARGETS[pos] || null;
      }
    }
    const sum     = key => s.drills.reduce((a, d) => a + (parseFloat(d[key]) || 0), 0);
    const td      = sum('td'); const hid = sum('hid'); const sd = sum('sd');
    const hmld    = sum('hmld'); const sa = sum('sa'); const expl = sum('expl');
    const maxSpd  = Math.max(0, ...s.drills.map(d => parseFloat(d.max_speed) || 0));
    const mins    = s.drills.reduce((a, d) => a + parseTimeToMinutes(d.total_time), 0);
    const pct     = (v, t) => t && mins > 0 ? (v / mins) / (t / 90) * 100 : null;
    const atLeast = (v, t) => !!(t && mins > 0 && v >= t * (mins / 90));
    return {
      sessionId: s.sessionId, date: s.date, title: s.title,
      opponent: opp?.name || s.title || '—',
      td, hid, sd, hmld, sa, expl, maxSpd, mins,
      tdPct: pct(td,tgt?.TD), hidPct: pct(hid,tgt?.HID), sdPct: pct(sd,tgt?.SD),
      hmldPct: pct(hmld,tgt?.HMLD), saPct: pct(sa,tgt?.SA),
      tdGreen: atLeast(td,tgt?.TD), hidGreen: atLeast(hid,tgt?.HID), sdGreen: atLeast(sd,tgt?.SD),
      hmldGreen: atLeast(hmld,tgt?.HMLD), saGreen: atLeast(sa,tgt?.SA),
    };
  }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

const f0   = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());
const f1   = n => (n == null || isNaN(n) ? '—' : n.toFixed(1));
const fPct = n => (n == null || isNaN(n) ? '—' : Math.round(n) + '%');
const fmtDate = d => { if (!d) return '—'; const [y,m,day] = d.split('-'); return `${day}/${m}/${y}`; };

// ── Per-minute average row: sum(param) / sum(mins played) across rows ─────────
function computePerMinRow(rows) {
  const totalMins = rows.reduce((s, r) => s + (r.mins || 0), 0);
  if (!totalMins) return null;
  const perMin = key => rows.reduce((s, r) => s + (r[key] || 0), 0) / totalMins;
  return { td: perMin('td'), hid: perMin('hid'), sd: perMin('sd'), hmld: perMin('hmld'), sa: perMin('sa') };
}

// ── Pure CSS bar chart — scales automatically with match count ─────────────────
function PrintBarChart({ matchRows }) {
  const CHART_H = 100;
  const rows = [...matchRows].reverse(); // oldest first = left
  const maxVal = Math.max(1, ...rows.map(r => (r.hid || 0) + (r.expl || 0)));

  return (
    <div style={{ width: '100%', marginBottom: 6 }}>
      {/* Bars */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: CHART_H, borderBottom: '1.5px solid #d1d5db' }}>
        {rows.map((r, i) => {
          const total = (r.hid || 0) + (r.expl || 0);
          const barH  = total > 0 ? Math.max(3, Math.round((total / maxVal) * CHART_H)) : 3;
          const explH = total > 0 ? Math.round((r.expl / total) * barH) : 0;
          const hidH  = barH - explH;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: CHART_H }}>
              <div style={{ width: '80%', maxWidth: 40, height: barH, display: 'flex', flexDirection: 'column', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                <div style={{ height: explH, background: '#1e3a8a', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {explH >= 14 && <span style={{ fontSize: 6, color: '#fff', fontWeight: 700, lineHeight: 1 }}>{Math.round(r.expl)}</span>}
                </div>
                <div style={{ height: hidH, background: '#93c5fd', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {hidH >= 14 && <span style={{ fontSize: 6, color: '#1e3a8a', fontWeight: 700, lineHeight: 1 }}>{Math.round(r.hid)}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Labels */}
      <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', overflow: 'hidden' }}>
            <div style={{ fontSize: 7, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
              {r.opponent.split(' ')[0]}
            </div>
            <div style={{ fontSize: 6, color: '#9ca3af', lineHeight: 1.3 }}>
              {r.date ? `${r.date.slice(8)}-${r.date.slice(5, 7)}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Single player page ────────────────────────────────────────────────────────
function PlayerPage({ player, matchRows }) {
  const n        = matchRows.length;
  const fontSize = n <= 8 ? 10 : n <= 12 ? 9 : 8;
  const av       = key => n > 0 ? matchRows.reduce((s, r) => s + (r[key] || 0), 0) / n : null;

  const avgs   = { mins: av('mins'), td: av('td'), hid: av('hid'), sa: av('sa') };
  const avgRow = n > 0 ? {
    td: av('td'), hid: av('hid'), sd: av('sd'), hmld: av('hmld'), sa: av('sa'),
    maxSpd: av('maxSpd'), mins: av('mins'),
    tdPct: av('tdPct'), hidPct: av('hidPct'), sdPct: av('sdPct'),
    hmldPct: av('hmldPct'), saPct: av('saPct'),
  } : null;
  const perMinRow = computePerMinRow(matchRows);

  const initials = name => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const GREEN    = { background: 'rgba(16,185,129,0.15)', color: '#065f46' };

  const TH = {
    fontSize: Math.max(7, fontSize - 1), fontWeight: 700, letterSpacing: '0.05em',
    textTransform: 'uppercase', color: '#6b7280', padding: '5px 6px',
    textAlign: 'right', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', whiteSpace: 'nowrap',
  };
  const THL = { ...TH, textAlign: 'left' };
  const TD_ = { fontSize, color: '#111827', textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid #f3f4f6' };
  const TDL = { ...TD_, textAlign: 'left' };

  return (
    <section className="player-page" style={{ fontFamily: 'Inter, Arial, sans-serif', color: '#111827', background: 'white' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2.5px solid #2563eb', paddingBottom: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Match Chronic</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.1, marginTop: 2 }}>{player?.name || '—'}</div>
        </div>
        <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="Heracles Almelo"
          style={{ height: 42, width: 'auto', objectFit: 'contain' }} />
      </div>

      {/* Player info */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 12, alignItems: 'flex-start' }}>
        {player?.photo_url ? (
          <img src={player.photo_url} alt={player.name}
            style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center top', border: '2px solid #e5e7eb', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#2563eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 24, color: '#fff' }}>
            {initials(player?.name)}
          </div>
        )}
        <div style={{ paddingTop: 2 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{player?.name}</div>
          {player?.position && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#dbeafe', color: '#1d4ed8', display: 'inline-block', marginTop: 4, marginBottom: 8, letterSpacing: '0.05em' }}>
              {player.position}
            </span>
          )}
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 9, color: '#374151', marginTop: 4 }}>
            <span><strong>{f1(avgs.mins)}</strong> min avg</span>
            <span><strong>{f0(avgs.td)}</strong> m TD avg</span>
            <span><strong>{f0(avgs.hid)}</strong> m HID avg</span>
            <span><strong>{f0(avgs.sa)}</strong> SA avg</span>
          </div>
        </div>
      </div>

      {/* Bar chart */}
      {matchRows.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 14, fontSize: 8, color: '#6b7280', marginBottom: 5 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: '#93c5fd', display: 'inline-block' }} />
              HID &gt;19.8 km/h
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: '#1e3a8a', display: 'inline-block' }} />
              Explosive Distance
            </span>
          </div>
          <PrintBarChart matchRows={matchRows} />
        </>
      )}

      {/* Match history table */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, marginTop: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#111827' }}>Match History</span>
        <span style={{ fontSize: 8, color: '#9ca3af' }}>{n} match{n !== 1 ? 'es' : ''}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={THL}>Opponent</th>
            <th style={TH}>Date</th>
            <th style={TH}>TD</th><th style={TH}>TD%</th>
            <th style={TH}>HID</th><th style={TH}>HID%</th>
            <th style={TH}>SD</th><th style={TH}>SD%</th>
            <th style={TH}>HMLD</th><th style={TH}>HMLD%</th>
            <th style={TH}>SA</th><th style={TH}>SA%</th>
            <th style={TH}>Max Spd</th>
            <th style={TH}>Mins</th>
          </tr>
        </thead>
        <tbody>
          {matchRows.map((r, i) => (
            <tr key={r.sessionId} style={{ background: i % 2 !== 0 ? '#f9fafb' : 'white' }}>
              <td style={{ ...TDL, fontWeight: 600 }}>{r.opponent}</td>
              <td style={{ ...TD_, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
              <td style={{ ...TD_, ...(r.tdGreen   ? GREEN : {}) }}>{f0(r.td)}</td>
              <td style={{ ...TD_, ...(r.tdGreen   ? GREEN : {}) }}>{fPct(r.tdPct)}</td>
              <td style={{ ...TD_, ...(r.hidGreen  ? GREEN : {}) }}>{f0(r.hid)}</td>
              <td style={{ ...TD_, ...(r.hidGreen  ? GREEN : {}) }}>{fPct(r.hidPct)}</td>
              <td style={{ ...TD_, ...(r.sdGreen   ? GREEN : {}) }}>{f0(r.sd)}</td>
              <td style={{ ...TD_, ...(r.sdGreen   ? GREEN : {}) }}>{fPct(r.sdPct)}</td>
              <td style={{ ...TD_, ...(r.hmldGreen ? GREEN : {}) }}>{f0(r.hmld)}</td>
              <td style={{ ...TD_, ...(r.hmldGreen ? GREEN : {}) }}>{fPct(r.hmldPct)}</td>
              <td style={{ ...TD_, ...(r.saGreen   ? GREEN : {}) }}>{f0(r.sa)}</td>
              <td style={{ ...TD_, ...(r.saGreen   ? GREEN : {}) }}>{fPct(r.saPct)}</td>
              <td style={TD_}>{f1(r.maxSpd)}</td>
              <td style={TD_}>{f1(r.mins)}</td>
            </tr>
          ))}
          {avgRow && (
            <tr style={{ background: 'rgba(37,99,235,0.05)', borderTop: '2px solid #e5e7eb' }}>
              <td style={{ ...TDL, fontWeight: 700, color: '#6b7280' }}>Season Average</td>
              <td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.td)}</td>
              <td style={TD_}>{fPct(avgRow.tdPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.hid)}</td>
              <td style={TD_}>{fPct(avgRow.hidPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.sd)}</td>
              <td style={TD_}>{fPct(avgRow.sdPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.hmld)}</td>
              <td style={TD_}>{fPct(avgRow.hmldPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.sa)}</td>
              <td style={TD_}>{fPct(avgRow.saPct)}</td>
              <td style={TD_}>{f1(avgRow.maxSpd)}</td>
              <td style={TD_}>{f1(avgRow.mins)}</td>
            </tr>
          )}
          {perMinRow && (
            <tr style={{ background: 'rgba(37,99,235,0.05)' }}>
              <td style={{ ...TDL, fontWeight: 700, color: '#6b7280' }}>Avg / Min Played</td>
              <td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(perMinRow.td)}</td>
              <td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.hid)}</td>
              <td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.sd)}</td>
              <td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.hmld)}</td>
              <td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.sa)}</td>
              <td style={TD_}>—</td>
              <td style={TD_}>—</td>
              <td style={TD_}>—</td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

// ── CSS injected into the print page ─────────────────────────────────────────
const PRINT_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @media print {
    @page { size: A4 portrait; margin: 12mm; }
    body  { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important; }
    section.player-page { page-break-after: always; break-after: page; }
    section.player-page:last-child { page-break-after: avoid; break-after: avoid; }
  }

  @media screen {
    body { background: #d1d5db; font-family: Inter, Arial, sans-serif; }
    .loading-screen {
      display: flex; align-items: center; justify-content: center;
      height: 100vh; font-family: Inter, Arial, sans-serif;
      font-size: 15px; color: #6b7280; gap: 12px;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner {
      width: 22px; height: 22px; border: 3px solid #e5e7eb;
      border-top-color: #2563eb; border-radius: 50%;
      animation: spin 0.75s linear infinite; flex-shrink: 0;
    }
    section.player-page {
      width: 210mm; min-height: 270mm;
      margin: 24px auto; background: white;
      box-shadow: 0 4px 24px rgba(0,0,0,0.18);
      padding: 14mm; box-sizing: border-box;
    }
  }
`;

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MatchChronicPrint() {
  const [pages,  setPages]  = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    let raw;
    try { raw = JSON.parse(localStorage.getItem('print_players') || '[]'); }
    catch { raw = []; }

    if (!raw.length) {
      setErrMsg('No players selected. Close this tab and try again.');
      setStatus('error');
      return;
    }

    Promise.all([getPlayers(), getOpponents(), getScenarios(), getLoadScenarios()])
      .then(async ([pl, opps, scens, ct]) => {
        const allPlayers = (pl  || []).filter(p => p.active !== false);
        const opponents  = opps || [];
        const scenarios  = (scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id }));
        const templates  = ct  || [];

        // Preserve selection order
        const selected = raw
          .map(id => allPlayers.find(p => String(p.id) === String(id)))
          .filter(Boolean);

        const results = await Promise.all(
          selected.map(async player => {
            let drills = [];
            try { drills = await getPlayerMatchDrills(player.name, 'match') || []; }
            catch { /* skip */ }
            return { player, matchRows: computeMatchRows(drills, player, opponents, scenarios, templates) };
          })
        );

        setPages(results);
        setStatus('ready');
      })
      .catch(e => { setErrMsg(e.message); setStatus('error'); });
  }, []);

  // Auto-trigger print dialog once all pages are ready
  useEffect(() => {
    if (status !== 'ready') return;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />

      {status === 'loading' && (
        <div className="loading-screen no-print">
          <div className="spinner" />
          Preparing player reports…
        </div>
      )}

      {status === 'error' && (
        <div className="loading-screen no-print" style={{ color: '#ef4444' }}>
          {errMsg}
        </div>
      )}

      {status === 'ready' && pages.map(({ player, matchRows }) => (
        <PlayerPage key={player.id} player={player} matchRows={matchRows} />
      ))}
    </>
  );
}
